import type { ComponentDefinition } from "../types";

/** Exact models with a documented primary chassis PE lug. Older saved copies
 * of these catalog definitions may predate that drawing terminal. */
export const MULTIPLUS_PRIMARY_PE_MODELS: ReadonlySet<string> = new Set([
  "shore-victron-multiplus-ii-12-3000-230",
  "shore-victron-multiplus-ii-12-3000-2x120",
]);

export const CHARGER: ComponentDefinition[] = [
  {
    id: "dcdc-generic",
    name: "Generic DC-DC Charger",
    shortName: "Generic DC-DC",
    subtitle: "Configurable DC-DC charger",
    category: "charger",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 60,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 60,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dcdc-victron-orion-18",
    name: "Victron Orion-Tr Smart 12/12-18",
    shortName: "Orion 18A",
    subtitle: "DC-DC charger 18A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr Smart 12/12-18",
    // Victron Orion-Tr Smart isolated manual §8: 8–17 V input,
    // 10–15 V adjustable output, 87% rated efficiency (12/12 models).
    // https://www.victronenergy.com/media/pg/Orion-Tr_Smart_DC-DC_Charger_-_Isolated/en/specifications.html
    inputVoltage: 12,
    outputVoltage: 12,
    compatibleVoltages: [12],
    conversionEfficiency: 0.87,
    ampOutput: 18,
    chargeAmpsTypical: 17,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 40,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 40,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 18,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 18,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
    image: "dcdc-victron-orion-18.webp",
  },
  {
    id: "dcdc-victron-orion-30",
    name: "Victron Orion-Tr Smart 12/12-30",
    shortName: "Orion 30A",
    subtitle: "DC-DC charger 30A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr Smart 12/12-30",
    // Victron Orion-Tr Smart isolated manual §8: 8–17 V input,
    // 10–15 V adjustable output, 87% rated efficiency (12/12 models).
    // https://www.victronenergy.com/media/pg/Orion-Tr_Smart_DC-DC_Charger_-_Isolated/en/specifications.html
    inputVoltage: 12,
    outputVoltage: 12,
    compatibleVoltages: [12],
    conversionEfficiency: 0.87,
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 60,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 60,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
    image: "dcdc-victron-orion-30.webp",
  },
  {
    id: "dcdc-victron-orion-xs-50",
    name: "Victron Orion XS 12/12-50",
    shortName: "Orion XS 50A",
    subtitle: "DC-DC charger 50A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion XS 12/12-50A",
    // Victron Orion XS manual §6.1: 98.5% efficiency, adjustable 50 A input limit.
    inputVoltage: 12,
    outputVoltage: 12,
    conversionEfficiency: 0.985,
    maxInputA: 50,
    ampOutput: 50,
    chargeAmpsTypical: 50,
    // Victron Orion XS installation §3.8: one physical GND connection.
    // Retain the saved input/output negative aliases without implying isolation.
    internalDcConnections: [["in-neg", "out-neg"]],
    // Victron Orion XS manual §§4.1, 4.4.2, 4.4.6: charger and engine
    // shutdown detection default enabled. "forced" models detection disabled.
    simulateStates: {
      default: "on",
      options: [
        { id: "on", label: "Auto", ampMultiplier: 1, role: "output" },
        { id: "forced", label: "Forced", ampMultiplier: 1, role: "output" },
        { id: "off", label: "Off", ampMultiplier: 0 },
      ],
    },
    width: 220,
    height: 120,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        // Orion XS 12/12-50A rev 11 §6.1: input limit adjustable up to 50 A.
        // The 60–70 A external fuse range is not the operating input current.
        maxAmp: 50,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 50,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
    image: "dcdc-victron-orion-xs-50.webp",
  },
  {
    id: "dcdc-victron-orion-24-12-70",
    name: "Victron Orion 24/12-70",
    shortName: "Orion 24/12-70",
    subtitle: "DC-DC converter 70A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "Orion 24/12-70 (840W)",
    // Victron high-power non-isolated DC/DC datasheet: 92% efficiency.
    inputVoltage: 24,
    outputVoltage: 12,
    conversionEfficiency: 0.92,
    // Victron ORI241270020 datasheet: 70 A continuous / 85 A peak out, ~40 A in at 24 V.
    ampOutput: 70,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 50,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 85,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 85,
      },
    ],
    image: "dcdc-victron-orion-24-12-70.webp",
  },
  {
    id: "dcdc-renogy-50s",
    name: "Renogy DCC50S",
    shortName: "DCC50S",
    subtitle: "DC-DC + MPPT charger 50A",
    category: "charger",
    manufacturer: "Renogy",
    model: "DCC50S",
    // Four physical power studs. Keep old drawing aliases connected to their
    // actual ALT+ / common NEG− studs (manual terminal diagram, printed p. 7).
    internalDcConnections: [["alt-pos", "start-pos"], ["pv-neg", "out-neg"]],
    ampOutput: 50,
    chargeAmpsTypical: 45,
    // Renogy RBC3050D1S manual, combined charging: 25 A per source.
    // https://eu.renogy.com/content/RBC3050D1S-Manual.pdf (p. 17)
    hybridSourceOutputLimitA: 25,
    pvMaxVoc: 25,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.2,
        maxAmp: 60,
      },
      {
        id: "start-pos",
        label: "ALT + (same stud)",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 60,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.65,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "NEG − (common)",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.85,
        maxAmp: 25,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "out-neg",
        label: "NEG − (common)",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
    image: "dcdc-renogy-50s.webp",
  },
  {
    id: "dcdc-enerdrive-dc2dc-40",
    name: "Enerdrive Dometic DC2DC 40+",
    shortName: "DC2DC 40+",
    subtitle: "DC-DC + MPPT charger, up to 50A",
    category: "charger",
    manufacturer: "Enerdrive / Dometic",
    model: "EN3DC40+",
    // EN3DC40+ manual rev 2.05, pp15–17, 20, 23, 33:
    // https://enerdrive.com.au/wp-content/uploads/2020/07/DC2DC40-User-Manual_V2.05_JAN-2026-1.pdf
    // Input limits are 50 A vehicle and 30 A PV; neither is a fuse rating.
    ampOutput: 50,
    chargeAmpsTypical: 40,
    pvMaxVoc: 45,
    // Per-terminal voltage domains differ; a component-wide [12] would
    // incorrectly reject the supported 24 V vehicle input.
    width: 240,
    height: 150,
    terminals: [
      { id: "in-pos", label: "CH3 START +", polarity: "positive", direction: "input", side: "left", offset: 0.2, maxAmp: 50 },
      { id: "in-neg", label: "CH3 START −", polarity: "negative", direction: "input", side: "left", offset: 0.4, maxAmp: 50 },
      { id: "pv-pos", label: "CH2 SOLAR +", polarity: "positive", direction: "input", side: "left", offset: 0.65, maxAmp: 30 },
      { id: "pv-neg", label: "CH2 SOLAR −", polarity: "negative", direction: "input", side: "left", offset: 0.85, maxAmp: 30 },
      { id: "out-pos", label: "CH1 BATT +", polarity: "positive", direction: "output", side: "right", offset: 0.3, maxAmp: 50 },
      { id: "out-neg", label: "CH1 BATT −", polarity: "negative", direction: "output", side: "right", offset: 0.7, maxAmp: 50 },
    ],
    image: "dcdc-enerdrive-dc2dc-40.webp",
  },
  {
    id: "dcdc-renogy-30s",
    name: "Renogy DCC30S",
    shortName: "DCC30S",
    subtitle: "DC-DC + MPPT charger 30A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Renogy",
    model: "DCC30S",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    pvMaxVoc: 25,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.2,
        maxAmp: 50,
      },
      {
        id: "start-pos",
        label: "START +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 50,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.65,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.85,
        maxAmp: 25,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
    image: "dcdc-renogy-30s.webp",
  },
  {
    id: "dcdc-renogy-1240",
    name: "Renogy DCC1240",
    shortName: "DCC1240",
    subtitle: "DC-DC charger 40A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Renogy",
    // Real SKU is RNG-DCC1212-40; "DCC1240" is not a Renogy model number.
    model: "RNG-DCC1212-40",
    ampOutput: 40,
    chargeAmpsTypical: 38,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "in-pos",
        label: "START +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 60,
      },
      {
        id: "in-neg",
        label: "START −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 60,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 40,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 40,
      },
    ],
  },
  {
    id: "dcdc-redarc-1240",
    name: "Redarc BCDC1240D",
    shortName: "BCDC1240D 40A",
    subtitle: "DC-DC + MPPT charger 40A",
    category: "charger",
    manufacturer: "Redarc",
    model: "BCDC1240D",
    ampOutput: 40,
    chargeAmpsTypical: 40,
    pvMaxVoc: 32,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 60,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.75,
        maxAmp: 25,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 40,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 40,
      },
    ],
    image: "dcdc-redarc-1240.webp",
  },
  {
    id: "dcdc-redarc-1250",
    name: "Redarc BCDC1250D",
    shortName: "BCDC1250D 50A",
    subtitle: "DC-DC + MPPT charger 50A",
    category: "charger",
    manufacturer: "Redarc",
    model: "BCDC1250D",
    ampOutput: 50,
    chargeAmpsTypical: 50,
    pvMaxVoc: 32,
    width: 220,
    height: 120,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.25,
        // Redarc BCDC dual-input manual: 60 A FK60 input fuse, peak draw ~55 A.
        maxAmp: 60,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 30,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.75,
        maxAmp: 30,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
    image: "dcdc-redarc-1250.webp",
  },
  {
    id: "dcdc-sterling-bb1230",
    name: "Sterling BB1230",
    shortName: "BB1230 30A",
    subtitle: "DC-DC charger 30A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Sterling Power",
    model: "BB1230",
    // Sterling BB1230 manual: rated by its 30 A INPUT draw; charge output ~22 A.
    ampOutput: 22,
    chargeAmpsTypical: 21,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 40,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 40,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 25,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 25,
      },
    ],
  },
  {
    id: "dcdc-kisae-dmt1230",
    name: "Kisae DMT1230",
    shortName: "DMT1230 30A",
    subtitle: "DC-DC + MPPT charger 30A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Kisae",
    model: "DMT1230",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    pvMaxVoc: 50,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 30,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.55,
        maxAmp: 30,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.75,
        maxAmp: 30,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
    image: "dcdc-kisae-dmt1230.webp",
  },
  {
    id: "mppt-generic-50",
    name: "MPPT solar controller 50A",
    shortName: "MPPT 50A",
    subtitle: "MPPT solar controller 50A",
    category: "charger",
    compatibleVoltages: [12, 24],
    ampOutput: 50,
    pvMaxVoc: 100,
    width: 215,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 60,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 50,
      },
    ],
    chargeAmpsTypical: 50,
  },
  {
    id: "mppt-victron-7510",
    name: "Victron MPPT 75/10",
    shortName: "MPPT 75/10",
    subtitle: "MPPT solar controller 10A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "SmartSolar 75/10",
    ampOutput: 10,
    pvMaxVoc: 75,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        // Victron SmartSolar 75/10 datasheet: max PV short-circuit current 13 A.
        maxAmp: 13,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 13,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 10,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 10,
      },
    ],
    chargeAmpsTypical: 10,
    image: "mppt-victron-7510.webp",
  },
  {
    id: "mppt-victron-7515",
    name: "Victron MPPT 75/15",
    shortName: "MPPT 75/15",
    subtitle: "MPPT solar controller 15A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "SmartSolar 75/15",
    ampOutput: 15,
    pvMaxVoc: 75,
    width: 190,
    height: 110,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 15,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    chargeAmpsTypical: 15,
    image: "mppt-victron-7515.webp",
  },
  {
    id: "mppt-victron-10020",
    name: "Victron MPPT 100/20",
    shortName: "MPPT 100/20",
    subtitle: "MPPT solar controller 20A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 100/20",
    ampOutput: 20,
    pvMaxVoc: 100,
    width: 195,
    height: 115,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        // Victron SmartSolar 100/20 datasheet: max PV short-circuit current 20 A.
        maxAmp: 20,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 20,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    chargeAmpsTypical: 20,
    image: "mppt-victron-10020.webp",
  },
  {
    id: "mppt-victron-10030",
    name: "Victron MPPT 100/30",
    shortName: "MPPT 100/30",
    subtitle: "MPPT solar controller 30A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "SmartSolar 100/30",
    ampOutput: 30,
    pvMaxVoc: 100,
    width: 200,
    height: 120,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    chargeAmpsTypical: 30,
    image: "mppt-victron-10030.webp",
  },
  {
    id: "mppt-victron-10050",
    name: "Victron MPPT 100/50",
    shortName: "MPPT 100/50",
    subtitle: "MPPT solar controller 50A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "SmartSolar 100/50",
    ampOutput: 50,
    pvMaxVoc: 100,
    width: 210,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 60,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 50,
      },
    ],
    chargeAmpsTypical: 50,
    image: "mppt-victron-10050.webp",
  },
  {
    id: "mppt-victron-15035",
    name: "Victron MPPT 150/35",
    shortName: "MPPT 150/35",
    subtitle: "MPPT solar controller 35A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/35",
    ampOutput: 35,
    pvMaxVoc: 150,
    width: 200,
    height: 120,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 35,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 35,
      },
    ],
    chargeAmpsTypical: 35,
    image: "mppt-victron-15035.webp",
  },
  {
    id: "mppt-victron-15045",
    name: "Victron MPPT 150/45",
    shortName: "MPPT 150/45",
    subtitle: "MPPT solar controller 45A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/45",
    ampOutput: 45,
    pvMaxVoc: 150,
    width: 210,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 45,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 45,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 45,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 45,
      },
    ],
    chargeAmpsTypical: 45,
  },
  {
    id: "mppt-victron-15070",
    name: "Victron MPPT 150/70",
    shortName: "MPPT 150/70",
    subtitle: "MPPT solar controller 70A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/70",
    ampOutput: 70,
    pvMaxVoc: 150,
    width: 230,
    height: 135,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 70,
      },
    ],
    chargeAmpsTypical: 70,
  },
  {
    id: "mppt-victron-25085",
    name: "Victron MPPT 250/85",
    shortName: "MPPT 250/85",
    subtitle: "MPPT solar controller 85A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 250/85",
    ampOutput: 85,
    pvMaxVoc: 250,
    width: 240,
    height: 140,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 85,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 85,
      },
    ],
    chargeAmpsTypical: 85,
    image: "mppt-victron-25085.webp",
  },
  {
    id: "mppt-victron-250100",
    name: "Victron MPPT 250/100",
    shortName: "MPPT 250/100",
    subtitle: "MPPT solar controller 100A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 250/100",
    ampOutput: 100,
    pvMaxVoc: 250,
    width: 250,
    height: 145,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 100,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 100,
      },
    ],
    chargeAmpsTypical: 100,
    image: "mppt-victron-250100.webp",
  },
  {
    id: "mppt-renogy-rover-40",
    name: "Renogy Rover Elite 40A",
    shortName: "Rover 40A",
    subtitle: "MPPT solar controller 40A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Rover Elite 40A",
    ampOutput: 40,
    pvMaxVoc: 100,
    width: 210,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 40,
      },
    ],
    chargeAmpsTypical: 40,
  },
  {
    id: "mppt-renogy-rover-60",
    name: "Renogy Rover 60A",
    shortName: "Rover 60A",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 36, 48],
    manufacturer: "Renogy",
    model: "Rover 60A",
    ampOutput: 60,
    // RNG-CTRL-RVR60-G1, manual A5 (2025-04-15), printed p. 54.
    pvMaxVoc: 145,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
    image: "mppt-renogy-rover-60.webp",
  },
  {
    id: "mppt-renogy-wanderer-30",
    name: "Renogy Wanderer 30A",
    shortName: "Wanderer 30A",
    subtitle: "PWM solar controller 30A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Renogy",
    model: "Wanderer 30A",
    ampOutput: 30,
    pvMaxVoc: 25,
    width: 200,
    height: 120,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    chargeAmpsTypical: 30,
    image: "mppt-renogy-wanderer-30.webp",
  },
  {
    id: "mppt-epever-4210",
    name: "Epever Tracer 4210AN",
    shortName: "Tracer 4210AN",
    subtitle: "MPPT solar controller 40A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Epever",
    model: "Tracer 4210AN",
    ampOutput: 40,
    pvMaxVoc: 100,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 40,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 40,
      },
    ],
    chargeAmpsTypical: 40,
    image: "mppt-epever-4210.webp",
  },
  {
    id: "mppt-outback-fm80",
    name: "Outback FlexMax 80",
    shortName: "FlexMax 80",
    subtitle: "MPPT solar controller 80A",
    category: "charger",
    compatibleVoltages: [12, 24, 36, 48, 60],
    manufacturer: "OutBack Power",
    model: "FlexMax 80",
    ampOutput: 80,
    pvMaxVoc: 150,
    width: 240,
    height: 145,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 90,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 90,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 80,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 80,
      },
    ],
    chargeAmpsTypical: 80,
    image: "mppt-outback-fm80.webp",
  },
  {
    id: "mppt-morningstar-60",
    name: "Morningstar TS-MPPT-60",
    shortName: "TriStar 60",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Morningstar",
    model: "TS-MPPT-60",
    ampOutput: 60,
    pvMaxVoc: 150,
    width: 230,
    height: 135,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
  },
  {
    id: "mppt-epever-tracer-3906bp",
    name: "EPEVER Tracer 3906BP",
    shortName: "Tracer 3906BP",
    subtitle: "MPPT solar controller 15A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "EPEVER",
    model: "Tracer 3906BP",
    // EPEVER Tracer-BP datasheet: rated charge 15 A despite the model number; PV Voc 60 V.
    ampOutput: 15,
    pvMaxVoc: 60,
    chargeAmpsTypical: 14,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 15,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.2,
        maxAmp: 15,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.4,
        maxAmp: 15,
      },
      {
        id: "load-pos",
        label: "LOAD +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.6,
        maxAmp: 15,
      },
      {
        id: "load-neg",
        label: "LOAD −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.8,
        maxAmp: 15,
      },
    ],
  },
  {
    id: "shore-victron-ip43-30",
    name: "Victron Phoenix IP43 30A",
    shortName: "Phoenix 30A",
    subtitle: "AC battery charger 30A",
    category: "charger",
    manufacturer: "Victron",
    model: "Phoenix Smart IP43 12/30",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 210,
    height: 120,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 16,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 16,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    image: "shore-victron-ip43-30.webp",
  },
  {
    id: "shore-victron-ip43-30-1plus1",
    name: "Victron Phoenix Smart IP43 12/30 (1+1)",
    shortName: "Phoenix 30A (1+1)",
    subtitle: "AC charger 30A, 4A start limit",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Victron",
    model: "Phoenix Smart IP43 12/30 (1+1)",
    ampOutput: 30,
    // Smart IP43 120–240 V manual §4.7: main 30 A, auxiliary 4 A, total 30 A.
    // https://www.victronenergy.com/media/pg/Smart_IP43_Charger_120-240V/en/operation.html
    outputCurrentLimitsA: { "batt-pos": 30, "start-pos": 4 },
    chargeAmpsTypical: 28,
    width: 210,
    height: 135,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 16,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 16,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.2,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.4,
        maxAmp: 30,
      },
      {
        id: "start-pos",
        label: "START +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 4,
      },
      {
        id: "start-neg",
        label: "START −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.85,
        maxAmp: 4,
      },
    ],
    image: "shore-victron-ip43-30.webp",
  },
  {
    id: "shore-victron-ip43-50",
    name: "Victron Phoenix IP43 50A",
    shortName: "Phoenix 50A",
    subtitle: "AC battery charger 50A",
    category: "charger",
    manufacturer: "Victron",
    model: "Phoenix Smart IP43 12/50",
    ampOutput: 50,
    chargeAmpsTypical: 47,
    width: 220,
    height: 125,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 16,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 16,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 50,
      },
    ],
    image: "shore-victron-ip43-50.webp",
  },
  {
    id: "shore-victron-bs-ip22-15-3",
    internalDcConnections: [["batt1-neg", "batt2-neg"], ["batt1-neg", "batt3-neg"]],
    name: "Victron Blue Smart IP22 12/15 (3)",
    shortName: "BS IP22 15-3",
    subtitle: "AC charger 15A, 3 isolated outputs",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP22 12/15-3",
    ampOutput: 15,
    chargeAmpsTypical: 14,
    width: 220,
    height: 140,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 5,
      },
      {
        id: "batt1-pos",
        label: "BATT 1 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.15,
        maxAmp: 15,
      },
      {
        id: "batt1-neg",
        label: "BATT 1 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 15,
      },
      {
        id: "batt2-pos",
        label: "BATT 2 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.45,
        maxAmp: 15,
      },
      {
        id: "batt2-neg",
        label: "BATT 2 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.6,
        maxAmp: 15,
      },
      {
        id: "batt3-pos",
        label: "BATT 3 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.75,
        maxAmp: 15,
      },
      {
        id: "batt3-neg",
        label: "BATT 3 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.9,
        maxAmp: 15,
      },
    ],
    image: "shore-victron-bs-ip22-15-3.webp",
  },
  {
    id: "shore-victron-multiplus-3000",
    acVoltage: 120,
    name: "Victron MultiPlus-II 12/3000 120V",
    shortName: "MultiPlus 3000",
    subtitle: "Inverter/charger 3000VA",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus-II 12/3000/120-50 120V",
    ampOutput: 120,
    watts: 2400,
    compatibleVoltages: [12],
    width: 250,
    height: 150,
    terminals: [
      {
        id: "ac-in-l",
        label: "AC IN L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.18,
        maxAmp: 50,
      },
      {
        id: "ac-in-n",
        label: "AC IN N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.32,
        maxAmp: 50,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.6,
        maxAmp: 400,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.8,
        maxAmp: 400,
      },
      {
        id: "ac-out-l",
        label: "AC OUT 1 L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.35,
        // 120 V manual rev. 15, §4.4 AC-OUT-1 table (not inverter-only output).
        maxAmp: 50,
      },
      {
        id: "ac-out-n",
        label: "AC OUT 1 N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.65,
        // 120 V manual rev. 15, §4.4 AC-OUT-1 table (not inverter-only output).
        maxAmp: 50,
      },
    ],
    ampDraw: 215,
    ampSurge: 500,
    // Existing photo is labeled 230 V / 32 A. Use the blueprint until a
    // manufacturer image of this exact single-120 V model is verified.
  },
  {
    id: "shore-victron-multiplus-ii-12-3000-230",
    acVoltage: 230,
    name: "Victron MultiPlus-II 12/3000 230V",
    shortName: "MultiPlus-II 230V",
    subtitle: "Inverter/charger 3000VA, 230V",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus-II 12/3000/120-32 230V",
    // Exact 12 V column: 2400 W continuous, 5500 W peak, 93% maximum efficiency.
    // https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/technical-specifications-mp-ii-230v.html
    // Estimates at nominal 12 V; maximum efficiency is not a guaranteed curve.
    ampDraw: Math.round(2400 / (12 * 0.93)),
    ampSurge: Math.round(5500 / (12 * 0.93)),
    ampOutput: 120,
    watts: 2400,
    compatibleVoltages: [12],
    width: 250,
    height: 150,
    terminals: [
      // AC ratings: installation §4.4, 32 A input and 45 A AC-out-1 with PowerAssist.
      // https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/installation.html
      { id: "ac-in-l", label: "AC IN L", polarity: "neutral", direction: "input", side: "left", offset: 0.18, maxAmp: 32 },
      { id: "ac-in-n", label: "AC IN N", polarity: "neutral", direction: "input", side: "left", offset: 0.32, maxAmp: 32 },
      // The model needs a ceiling for DC current resolution. This estimated
      // envelope avoids clipping the peak at 9.5 V; it is NOT a connector rating.
      { id: "dc-pos", label: "DC +", polarity: "positive", direction: "input", side: "left", offset: 0.6, maxAmp: Math.ceil(5500 / (9.5 * 0.93)) },
      { id: "dc-neg", label: "DC −", polarity: "negative", direction: "input", side: "left", offset: 0.8, maxAmp: Math.ceil(5500 / (9.5 * 0.93)) },
      { id: "ac-out-l", label: "AC OUT 1 L", polarity: "neutral", direction: "output", side: "right", offset: 0.35, maxAmp: 45 },
      { id: "ac-out-n", label: "AC OUT 1 N", polarity: "neutral", direction: "output", side: "right", offset: 0.65, maxAmp: 45 },
      // 230 V manual Appendix A, item F: primary M6 earth connection.
      // Neutral bonding is relay-controlled. No permanent DC−/PE link is declared.
      // https://www.victronenergy.com/media/pg/MultiPlus-II_230V/en/appendix.html
      { id: "chassis", label: "CHASSIS / PE", polarity: "ground", direction: "bidi", side: "bottom", offset: 0.5, maxAmp: 0 },
    ],
    // Existing photo checked: nameplate reads 230 V / 32 A.
    image: "shore-victron-multiplus-3000.webp",
  },
  {
    id: "shore-victron-multiplus-ii-12-3000-2x120",
    acVoltage: 120,
    name: "Victron MultiPlus-II 12/3000 2x120V",
    shortName: "MultiPlus-II 2x120",
    subtitle: "Inverter/charger 3000VA, split-phase",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus-II 12/3000/120-50 2x120V",
    ampOutput: 120,
    watts: 2400,
    compatibleVoltages: [12],
    width: 250,
    height: 150,
    terminals: [
      {
        id: "ac-in-l1",
        label: "AC IN L1",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.12,
        maxAmp: 50,
      },
      {
        id: "ac-in-l2",
        label: "AC IN L2",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.24,
        maxAmp: 50,
      },
      {
        id: "ac-in-n",
        label: "AC IN N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.36,
        maxAmp: 50,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.62,
        maxAmp: 400,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.82,
        maxAmp: 400,
      },
      {
        id: "ac-out-l1",
        label: "AC OUT L1",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "ac-out-l2",
        label: "AC OUT L2",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.5,
        maxAmp: 30,
      },
      {
        id: "ac-out-n",
        label: "AC OUT N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
      // Exact 2x120 V manual §4.4 and Appendix A (E): primary M8 PE bolt.
      // https://www.victronenergy.com/media/pg/MultiPlus-II_2x120V/en/appendix.html
      // Keep chassis separate from DC− and the relay-controlled neutral bond.
      // Unknown ground-terminal ampacity is not a conductor-sizing rating.
      {
        id: "chassis",
        label: "CHASSIS / PE",
        polarity: "ground",
        direction: "bidi",
        side: "bottom",
        offset: 0.5,
        maxAmp: 0,
      },
    ],
    ampDraw: 215,
    ampSurge: 500,
    image: "shore-victron-multiplus-ii-12-3000-2x120.webp",
  },
  {
    id: "shore-victron-multiplus-12-1200-120",
    name: "Victron MultiPlus 12/1200 120V",
    shortName: "MultiPlus 1200 · 120V",
    subtitle: "1200 VA / 1000 W inverter, 50 A charger",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus 12/1200/50-16 120V",
    voltage: 12,
    acVoltage: 120,
    compatibleVoltages: [12],
    // Exact 120 V manual §7.1: 1000 W continuous, 2400 W peak, 93% max efficiency.
    // Nominal-12 V estimates, not measured input current or a guaranteed efficiency curve.
    // https://www.victronenergy.com/media/pg/MultiPlus_12V_500-1200VA_120Vac/en/technical-data.html
    watts: 1000,
    ampDraw: Math.round(1000 / (12 * 0.93)),
    ampSurge: Math.round(2400 / (12 * 0.93)),
    ampOutput: 50,
    width: 250,
    height: 200,
    image: "shore-victron-multiplus-12-1200-120.webp",
    terminals: [
      { id: "ac-in-l", label: "AC IN L", polarity: "neutral", direction: "input", side: "left", offset: 0.12, maxAmp: 16 },
      { id: "ac-in-n", label: "AC IN N", polarity: "neutral", direction: "input", side: "left", offset: 0.27, maxAmp: 16 },
      { id: "ac-in-pe", label: "AC IN PE", polarity: "ground", direction: "bidi", side: "left", offset: 0.42, maxAmp: 0 },
      // DC terminal ampacity is unpublished: zero means unknown, not a current limit.
      { id: "dc-pos", label: "DC +", polarity: "positive", direction: "bidi", side: "left", offset: 0.65, maxAmp: 0 },
      { id: "dc-neg", label: "DC −", polarity: "negative", direction: "bidi", side: "left", offset: 0.85, maxAmp: 0 },
      // 16 A is the transfer rating, not a verified maximum with PowerAssist.
      // Leave output ampacity unknown; complete AC protection needs separate design.
      { id: "ac-out-l", label: "AC OUT L", polarity: "neutral", direction: "output", side: "right", offset: 0.2, maxAmp: 0 },
      { id: "ac-out-n", label: "AC OUT N", polarity: "neutral", direction: "output", side: "right", offset: 0.4, maxAmp: 0 },
      { id: "ac-out-pe", label: "AC OUT PE", polarity: "ground", direction: "bidi", side: "right", offset: 0.6, maxAmp: 0 },
      { id: "chassis", label: "CHASSIS", polarity: "ground", direction: "bidi", side: "right", offset: 0.8, maxAmp: 0 },
    ],
  },
  {
    id: "shore-victron-multiplus-2000",
    name: "Victron MultiPlus 12/2000",
    shortName: "MultiPlus 2000",
    subtitle: "Inverter/charger 2000VA",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus 12/2000/80",
    ampOutput: 80,
    watts: 1600,
    compatibleVoltages: [12],
    width: 250,
    height: 150,
    terminals: [
      {
        id: "ac-in-l",
        label: "AC IN L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.18,
        maxAmp: 30,
      },
      {
        id: "ac-in-n",
        label: "AC IN N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.32,
        maxAmp: 30,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.6,
        maxAmp: 300,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.8,
        maxAmp: 300,
      },
      {
        id: "ac-out-l",
        label: "AC OUT L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 16,
      },
      {
        id: "ac-out-n",
        label: "AC OUT N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 16,
      },
    ],
    ampDraw: 145,
    // Victron MultiPlus 12/2000 120V datasheet: 3500 W peak ≈ 330 A at the 10.5 V cutoff.
    ampSurge: 330,
    image: "shore-victron-multiplus-2000.webp",
  },
  {
    id: "shore-noco-genius-10",
    name: "NOCO Genius10",
    shortName: "Genius10",
    subtitle: "AC battery charger 10A",
    category: "charger",
    manufacturer: "NOCO",
    model: "Genius10",
    ampOutput: 10,
    chargeAmpsTypical: 9,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 6,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 6,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 10,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 10,
      },
    ],
    image: "shore-noco-genius-10.webp",
  },
  {
    id: "shore-cteck-mxs-7",
    name: "CTEK MXS 7.0",
    shortName: "MXS 7.0",
    subtitle: "AC battery charger 7A",
    category: "charger",
    manufacturer: "CTEK",
    model: "MXS 7.0",
    ampOutput: 7.2,
    chargeAmpsTypical: 7.2,
    width: 180,
    height: 100,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 4,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 4,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 7.2,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 7.2,
      },
    ],
    image: "shore-cteck-mxs-7.webp",
  },
  {
    id: "dcdc-redarc-alpha75",
    name: "Redarc Manager Alpha75",
    shortName: "Alpha75",
    subtitle: "DC-DC + MPPT + AC charger 75A",
    category: "charger",
    manufacturer: "Redarc",
    model: "BMS12075",
    ampOutput: 75,
    chargeAmpsTypical: 70,
    watts: 1200,
    // REDARC INST333-7 pp. 7, 9, 28, 43: one common GROUND terminal,
    // 82 A maximum vehicle input and one 75 A auxiliary output stage.
    // Keep both legacy negative IDs so saved drawings retain their cables.
    internalDcConnections: [["pv-neg", "batt-neg"]],
    maxInputA: 82,
    outputVoltage: 12,
    // REDARC INST333-7 p. 43: cold-weather array Voc must remain below 48 V.
    pvMaxVoc: 48,
    width: 240,
    height: 160,
    terminals: [
      {
        id: "alt-pos",
        label: "START +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.18,
        maxAmp: 100,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.36,
        maxAmp: 82,
      },
      {
        id: "pv-neg",
        label: "GROUND (common)",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 82,
      },
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.66,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.78,
        maxAmp: 16,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.9,
        maxAmp: 16,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 100,
      },
      {
        id: "batt-neg",
        label: "GROUND (common)",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 100,
      },
    ],
  },
  {
    id: "mppt-victron-10015",
    name: "Victron MPPT 100/15",
    shortName: "MPPT 100/15",
    subtitle: "MPPT solar controller 15A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Victron",
    model: "SmartSolar 100/15",
    ampOutput: 15,
    pvMaxVoc: 100,
    width: 190,
    height: 110,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 15,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    chargeAmpsTypical: 15,
    image: "mppt-victron-10015.webp",
  },
  {
    id: "mppt-victron-15060",
    name: "Victron MPPT 150/60",
    shortName: "MPPT 150/60",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/60",
    ampOutput: 60,
    pvMaxVoc: 150,
    width: 230,
    height: 135,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
    image: "mppt-victron-15060.webp",
  },
  {
    id: "mppt-victron-15085",
    name: "Victron MPPT 150/85",
    shortName: "MPPT 150/85",
    subtitle: "MPPT solar controller 85A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/85",
    ampOutput: 85,
    pvMaxVoc: 150,
    width: 240,
    height: 140,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 85,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 85,
      },
    ],
    chargeAmpsTypical: 85,
    image: "mppt-victron-15085.webp",
  },
  {
    id: "mppt-victron-150100",
    name: "Victron MPPT 150/100",
    shortName: "MPPT 150/100",
    subtitle: "MPPT solar controller 100A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 150/100",
    ampOutput: 100,
    pvMaxVoc: 150,
    width: 250,
    height: 145,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 100,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 100,
      },
    ],
    chargeAmpsTypical: 100,
    image: "mppt-victron-150100.webp",
  },
  {
    id: "mppt-victron-25060",
    name: "Victron MPPT 250/60",
    shortName: "MPPT 250/60",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 250/60",
    ampOutput: 60,
    pvMaxVoc: 250,
    width: 240,
    height: 140,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        // Victron SmartSolar 250/60-250/70 datasheet: max PV short-circuit current 35 A.
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
    image: "mppt-victron-25060.webp",
  },
  {
    id: "mppt-victron-25070",
    name: "Victron MPPT 250/70",
    shortName: "MPPT 250/70",
    subtitle: "MPPT solar controller 70A",
    category: "charger",
    compatibleVoltages: [12, 24, 48],
    manufacturer: "Victron",
    model: "SmartSolar 250/70",
    ampOutput: 70,
    pvMaxVoc: 250,
    width: 245,
    height: 142,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        // Victron SmartSolar 250/60-250/70 datasheet: max PV short-circuit current 35 A.
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 70,
      },
    ],
    chargeAmpsTypical: 70,
    image: "mppt-victron-25070.webp",
  },
  {
    id: "mppt-victron-rs-450-100",
    name: "Victron MPPT RS 450/100",
    shortName: "MPPT RS 100",
    subtitle: "High-voltage MPPT 100A",
    category: "charger",
    manufacturer: "Victron",
    model: "SmartSolar MPPT RS 450/100",
    ampOutput: 100,
    pvMaxVoc: 450,
    compatibleVoltages: [48],
    width: 260,
    height: 160,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 18,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 18,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 100,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 100,
      },
    ],
    chargeAmpsTypical: 100,
    image: "mppt-victron-rs-450-100.webp",
  },
  {
    id: "mppt-victron-rs-450-200",
    name: "Victron MPPT RS 450/200",
    shortName: "MPPT RS 200",
    subtitle: "High-voltage MPPT 200A",
    category: "charger",
    manufacturer: "Victron",
    model: "SmartSolar MPPT RS 450/200",
    ampOutput: 200,
    pvMaxVoc: 450,
    compatibleVoltages: [48],
    width: 280,
    height: 170,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        // Victron SmartSolar MPPT RS datasheet: max operational PV 18 A per tracker.
        maxAmp: 18,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 18,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 200,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 200,
      },
    ],
    chargeAmpsTypical: 200,
    image: "mppt-victron-rs-450-200.webp",
  },
  {
    id: "shore-victron-bs-ip22-15-1",
    name: "Victron Blue Smart IP22 12/15 (1)",
    shortName: "BS IP22 15-1",
    subtitle: "AC battery charger 15A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP22 12/15-1",
    ampOutput: 15,
    chargeAmpsTypical: 14,
    width: 210,
    height: 120,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "shore-victron-bs-ip22-15-1.webp",
  },
  {
    id: "shore-victron-bs-ip22-20",
    name: "Victron Blue Smart IP22 12/20",
    shortName: "BS IP22 20",
    subtitle: "AC battery charger 20A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP22 12/20-1",
    ampOutput: 20,
    chargeAmpsTypical: 19,
    width: 215,
    height: 122,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 6,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 6,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 6,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    image: "shore-victron-bs-ip22-20.webp",
  },
  {
    id: "shore-victron-bs-ip22-30-1",
    name: "Victron Blue Smart IP22 12/30 (1)",
    shortName: "BS IP22 30-1",
    subtitle: "AC battery charger 30A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP22 12/30-1",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 220,
    height: 125,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 10,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 10,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 10,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    image: "shore-victron-bs-ip22-30-1.webp",
  },
  {
    id: "shore-victron-bs-ip22-30-3",
    name: "Victron Blue Smart IP22 12/30 (3)",
    shortName: "BS IP22 30-3",
    subtitle: "AC charger 30A, 3 isolated outputs",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP22 12/30-3",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 230,
    height: 145,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 10,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 10,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 10,
      },
      {
        id: "batt1-pos",
        label: "BATT 1 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.15,
        maxAmp: 30,
      },
      {
        id: "batt1-neg",
        label: "BATT 1 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "batt2-pos",
        label: "BATT 2 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.45,
        maxAmp: 30,
      },
      {
        id: "batt2-neg",
        label: "BATT 2 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.6,
        maxAmp: 30,
      },
      {
        id: "batt3-pos",
        label: "BATT 3 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.75,
        maxAmp: 30,
      },
      {
        id: "batt3-neg",
        label: "BATT 3 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.9,
        maxAmp: 30,
      },
    ],
    image: "shore-victron-bs-ip22-30-3.webp",
  },
  {
    id: "shore-victron-bs-ip65-7",
    name: "Victron Blue Smart IP65 12/7",
    shortName: "BS IP65 7",
    subtitle: "Portable AC charger 7A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP65 12/7",
    ampOutput: 7,
    chargeAmpsTypical: 7,
    width: 180,
    height: 95,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 3,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 3,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 7,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 7,
      },
    ],
    image: "shore-victron-bs-ip65-7.webp",
  },
  {
    id: "shore-victron-bs-ip65-15",
    name: "Victron Blue Smart IP65 12/15",
    shortName: "BS IP65 15",
    subtitle: "Portable AC charger 15A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP65 12/15",
    ampOutput: 15,
    chargeAmpsTypical: 14,
    width: 190,
    height: 105,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 15,
      },
    ],
    image: "shore-victron-bs-ip65-15.webp",
  },
  {
    id: "shore-victron-bs-ip67-12-7",
    name: "Victron Blue Smart IP67 12/7",
    shortName: "BS IP67 7",
    subtitle: "Waterproof AC charger 7A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Victron",
    model: "Blue Smart IP67 12/7",
    ampOutput: 7,
    chargeAmpsTypical: 7,
    width: 210,
    height: 95,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 3,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 3,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 3,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 7,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 7,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip67-12-13",
    name: "Victron Blue Smart IP67 12/13",
    shortName: "BS IP67 13",
    subtitle: "Waterproof AC charger 13A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Victron",
    model: "Blue Smart IP67 12/13",
    ampOutput: 13,
    chargeAmpsTypical: 13,
    width: 210,
    height: 95,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 13,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 13,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip67-12-17",
    name: "Victron Blue Smart IP67 12/17",
    shortName: "BS IP67 17",
    subtitle: "Waterproof AC charger 17A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Victron",
    model: "Blue Smart IP67 12/17",
    ampOutput: 17,
    chargeAmpsTypical: 16,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 17,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 17,
      },
    ],
    image: "shore-victron-bs-ip67.webp",
  },
  {
    id: "shore-victron-bs-ip67-12-25",
    name: "Victron Blue Smart IP67 12/25",
    shortName: "BS IP67 25",
    subtitle: "Waterproof AC charger 25A",
    category: "charger",
    compatibleVoltages: [12],
    manufacturer: "Victron",
    model: "Blue Smart IP67 12/25",
    ampOutput: 25,
    chargeAmpsTypical: 24,
    width: 220,
    height: 110,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 8,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 8,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 8,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 25,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip67-24-5",
    name: "Victron Blue Smart IP67 24/5",
    shortName: "BS IP67 24/5",
    subtitle: "Waterproof AC charger 24V 5A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP67 24/5",
    ampOutput: 5,
    chargeAmpsTypical: 5,
    compatibleVoltages: [24],
    width: 210,
    height: 95,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 3,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 3,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 3,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 5,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 5,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip67-24-8",
    name: "Victron Blue Smart IP67 24/8",
    shortName: "BS IP67 24/8",
    subtitle: "Waterproof AC charger 24V 8A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP67 24/8",
    ampOutput: 8,
    chargeAmpsTypical: 8,
    compatibleVoltages: [24],
    width: 220,
    height: 110,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 8,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 8,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip67-24-12",
    name: "Victron Blue Smart IP67 24/12",
    shortName: "BS IP67 24/12",
    subtitle: "Waterproof AC charger 24V 12A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP67 24/12",
    ampOutput: 12,
    chargeAmpsTypical: 11,
    compatibleVoltages: [24],
    width: 220,
    height: 110,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 5,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 5,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        // Match the AC lead planning limit; not a verified PE contact rating.
        maxAmp: 5,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 12,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 12,
      },
    ],
  },
  {
    id: "shore-victron-ip43-50-3",
    name: "Victron Phoenix IP43 50A (3)",
    shortName: "Phoenix 50-3",
    subtitle: "AC charger 50A, 3 isolated outputs",
    category: "charger",
    manufacturer: "Victron",
    model: "Phoenix Smart IP43 12/50-3",
    ampOutput: 50,
    chargeAmpsTypical: 47,
    width: 235,
    height: 150,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.25,
        maxAmp: 16,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.5,
        maxAmp: 16,
      },
      {
        id: "ac-pe",
        label: "AC ⏚",
        polarity: "ground",
        direction: "bidi",
        side: "left",
        offset: 0.75,
        maxAmp: 16,
      },
      {
        id: "batt1-pos",
        label: "BATT 1 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.15,
        maxAmp: 50,
      },
      {
        id: "batt1-neg",
        label: "BATT 1 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "batt2-pos",
        label: "BATT 2 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.45,
        maxAmp: 50,
      },
      {
        id: "batt2-neg",
        label: "BATT 2 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.6,
        maxAmp: 50,
      },
      {
        id: "batt3-pos",
        label: "BATT 3 +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.75,
        maxAmp: 50,
      },
      {
        id: "batt3-neg",
        label: "BATT 3 −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.9,
        maxAmp: 50,
      },
    ],
    image: "shore-victron-ip43-50-3.webp",
  },
  {
    id: "dcdc-victron-orion-9",
    name: "Victron Orion-Tr 12/12-9",
    shortName: "Orion 9A",
    subtitle: "DC-DC charger 9A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr 12/12-9 (110W)",
    ampOutput: 9,
    width: 190,
    height: 105,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 20,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 9,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 9,
      },
    ],
    // No jumperDisabled setting: remote on/off pin, not engine-run detection.
    image: "dcdc-victron-orion-9.webp",
  },
  {
    // Not a real Victron product: unbranded so no SKU page generates, kept so saved systems load.
    id: "dcdc-victron-orion-15",
    name: "15A DC-DC Charger",
    shortName: "DC-DC 15A",
    subtitle: "DC-DC charger 15A",
    category: "charger",
    ampOutput: 15,
    chargeAmpsTypical: 14,
    width: 195,
    height: 108,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 35,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 35,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 15,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 15,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
  },
  {
    id: "dcdc-victron-orion-2412-15",
    internalDcConnections: [["in-neg", "out-neg"]],
    name: "Victron Orion-Tr 24/12-15",
    shortName: "Orion 24/12-15",
    subtitle: "DC-DC step-down converter 15A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr 24/12-15",
    // Manufacturer nominal ratings; constant efficiency is the planning model,
    // not a measured efficiency curve at every load or temperature.
    // https://www.victronenergy.com/upload/documents/Datasheet-Orion-Tr-DC-DC-converters-low-power,-non-isolated-EN.pdf
    inputVoltage: 24,
    outputVoltage: 12.5,
    conversionEfficiency: 0.97,
    ampOutput: 15,
    width: 195,
    height: 108,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 20,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 20,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 15,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 15,
      },
    ],
  },
  {
    id: "dcdc-victron-orion-1224-10",
    name: "Victron Orion-Tr Smart 12/24-10",
    shortName: "Orion 12/24-10",
    subtitle: "DC-DC step-up charger 24V 10A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr Smart 12/24-10",
    ampOutput: 10,
    chargeAmpsTypical: 9,
    width: 195,
    height: 108,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 30,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 12,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 12,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
    image: "dcdc-victron-orion-1224-10.webp",
  },
  {
    id: "dcdc-victron-orion-4812-30",
    name: "Victron Orion-Tr 48/12-30",
    shortName: "Orion 48/12-30",
    subtitle: "DC-DC step-down 48→12V 30A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr 48/12-30",
    // Victron isolated DC/DC converter datasheet, 360 W table: 87%.
    inputVoltage: 48,
    outputVoltage: 12,
    conversionEfficiency: 0.87,
    ampOutput: 30,
    chargeAmpsTypical: 28,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 12,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 12,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
  },
  {
    id: "dcdc-victron-orion-4824-12",
    name: "Victron Orion-Tr 48/24-12",
    shortName: "Orion 48/24-12",
    subtitle: "DC-DC step-down 48→24V 12A",
    category: "charger",
    manufacturer: "Victron",
    model: "Orion-Tr 48/24-12 (280W)",
    ampOutput: 12,
    chargeAmpsTypical: 11,
    width: 195,
    height: 108,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 8,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 8,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 12,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 12,
      },
    ],
  },
  {
    // Not a real Victron product: unbranded so no SKU page generates. Kept: seeds.ts uses the id.
    id: "dcdc-victron-orion-xs-25",
    name: "25A DC-DC Charger",
    shortName: "DC-DC 25A",
    subtitle: "DC-DC charger 25A",
    category: "charger",
    ampOutput: 25,
    chargeAmpsTypical: 24,
    width: 210,
    height: 115,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 40,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 40,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 25,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 25,
      },
    ],
    settings: [
      {
        id: "jumperDisabled",
        label: {
          key: "setting.orion.jumperDisabled.label",
        },
        help: {
          key: "setting.orion.jumperDisabled.help",
        },
        type: "toggle",
        default: false,
        semantics: "disables-output",
      },
    ],
  },
  {
    id: "dcdc-victron-bb-25",
    name: "Victron Buck-Boost 25A",
    shortName: "Buck-Boost 25",
    subtitle: "Smart-alternator DC-DC 25A",
    category: "charger",
    manufacturer: "Victron",
    model: "Buck-Boost DC-DC 25A",
    ampOutput: 25,
    chargeAmpsTypical: 24,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 50,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 25,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 25,
      },
    ],
    image: "dcdc-victron-bb-25.webp",
  },
  {
    id: "dcdc-victron-bb-50",
    name: "Victron Buck-Boost 50A",
    shortName: "Buck-Boost 50",
    subtitle: "Smart-alternator DC-DC 50A",
    category: "charger",
    manufacturer: "Victron",
    model: "Buck-Boost DC-DC 50A",
    ampOutput: 50,
    chargeAmpsTypical: 48,
    width: 215,
    height: 120,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 90,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 90,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
    image: "dcdc-victron-bb-50.webp",
  },
  {
    id: "dcdc-victron-bb-100",
    name: "Victron Buck-Boost 100A",
    shortName: "Buck-Boost 100",
    subtitle: "Smart-alternator DC-DC 100A",
    category: "charger",
    manufacturer: "Victron",
    model: "Buck-Boost DC-DC 100A",
    ampOutput: 100,
    chargeAmpsTypical: 95,
    width: 235,
    height: 130,
    terminals: [
      {
        id: "in-pos",
        label: "IN +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 180,
      },
      {
        id: "in-neg",
        label: "IN −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 180,
      },
      {
        id: "out-pos",
        label: "OUT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 100,
      },
      {
        id: "out-neg",
        label: "OUT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 100,
      },
    ],
    image: "dcdc-victron-bb-100.webp",
  },
  {
    // Not a real Renogy product: unbranded so no SKU page generates, kept so saved systems load.
    id: "dcdc-renogy-30d",
    name: "30A Dual-Input DC-DC + MPPT Charger",
    shortName: "DC-DC+MPPT 30A",
    subtitle: "Dual-input DC-DC + MPPT 30A",
    category: "charger",
    ampOutput: 30,
    chargeAmpsTypical: 28,
    pvMaxVoc: 25,
    width: 225,
    height: 130,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.2,
        maxAmp: 50,
      },
      {
        id: "alt-neg",
        label: "ALT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 50,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.65,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.85,
        maxAmp: 25,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 30,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 30,
      },
    ],
  },
  {
    // Not a real Renogy product: unbranded so no SKU page generates, kept so saved systems load.
    id: "dcdc-renogy-50d",
    name: "50A Dual-Input DC-DC + MPPT Charger",
    shortName: "DC-DC+MPPT 50A",
    subtitle: "Dual-input DC-DC + MPPT 50A",
    category: "charger",
    ampOutput: 50,
    chargeAmpsTypical: 47,
    pvMaxVoc: 25,
    width: 235,
    height: 135,
    terminals: [
      {
        id: "alt-pos",
        label: "ALT +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.2,
        maxAmp: 80,
      },
      {
        id: "alt-neg",
        label: "ALT −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.4,
        maxAmp: 80,
      },
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.85,
        maxAmp: 50,
      },
      {
        id: "out-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.3,
        maxAmp: 50,
      },
      {
        id: "out-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.7,
        maxAmp: 50,
      },
    ],
  },
  {
    id: "mppt-renogy-rover-20",
    name: "Renogy Rover 20A",
    shortName: "Rover 20A",
    subtitle: "MPPT solar controller 20A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Rover 20A",
    ampOutput: 20,
    pvMaxVoc: 100,
    width: 195,
    height: 115,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 25,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    chargeAmpsTypical: 20,
  },
  {
    id: "mppt-renogy-rover-30",
    image: "mppt-renogy-rover-30.webp",
    name: "Renogy Rover 30A",
    shortName: "Rover 30A",
    subtitle: "MPPT solar controller 30A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Rover 30A",
    ampOutput: 30,
    pvMaxVoc: 100,
    width: 205,
    height: 120,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 40,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    chargeAmpsTypical: 30,
  },
  {
    id: "mppt-renogy-rover-li-30",
    image: "mppt-renogy-rover-li-30.webp",
    name: "Renogy Rover Li 30A",
    shortName: "Rover Li 30",
    subtitle: "MPPT solar controller 30A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Rover Li 30A",
    ampOutput: 30,
    // Renogy Rover Li 30A product page: max PV input voltage 100 VDC.
    pvMaxVoc: 100,
    width: 205,
    height: 120,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 40,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    chargeAmpsTypical: 30,
  },
  {
    id: "mppt-renogy-rover-li-40",
    name: "Renogy Rover Li 40A",
    shortName: "Rover Li 40",
    subtitle: "MPPT solar controller 40A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Rover Li 40A",
    ampOutput: 40,
    pvMaxVoc: 100,
    width: 215,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 40,
      },
    ],
    chargeAmpsTypical: 40,
    image: "mppt-renogy-rover-li-40.webp",
  },
  {
    id: "mppt-renogy-rover-li-60",
    name: "Renogy Rover Li 60A",
    shortName: "Rover Li 60",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 36, 48],
    manufacturer: "Renogy",
    model: "Rover Li 60A",
    ampOutput: 60,
    // RNG-CTRL-RVR60-G1, manual A5 (2025-04-15), printed p. 54.
    pvMaxVoc: 145,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 70,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 70,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
    image: "mppt-renogy-rover-li-60.webp",
  },
  {
    id: "mppt-renogy-wanderer-10",
    name: "Renogy Wanderer 10A",
    shortName: "Wanderer 10A",
    subtitle: "PWM solar controller 10A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Wanderer 10A",
    ampOutput: 10,
    pvMaxVoc: 55,
    width: 180,
    height: 105,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 15,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 10,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 10,
      },
    ],
    chargeAmpsTypical: 10,
    image: "mppt-renogy-wanderer-10.webp",
  },
  {
    id: "mppt-renogy-adventurer-30",
    name: "Renogy Adventurer 30A",
    shortName: "Adventurer 30",
    subtitle: "PWM solar controller 30A, flush",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Adventurer Li 30A",
    ampOutput: 30,
    pvMaxVoc: 50,
    width: 200,
    height: 110,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 35,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 35,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 30,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 30,
      },
    ],
    chargeAmpsTypical: 30,
    image: "mppt-renogy-adventurer-30.webp",
  },
  {
    id: "mppt-renogy-voyager-10",
    name: "Renogy Voyager 10A",
    shortName: "Voyager 10",
    subtitle: "Waterproof PWM controller 10A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Voyager 10A",
    ampOutput: 10,
    pvMaxVoc: 25,
    width: 175,
    height: 100,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 15,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 15,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 10,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 10,
      },
    ],
    chargeAmpsTypical: 10,
    image: "mppt-renogy-voyager-10.webp",
  },
  {
    id: "mppt-renogy-voyager-20",
    name: "Renogy Voyager 20A",
    shortName: "Voyager 20",
    subtitle: "Waterproof PWM controller 20A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Voyager 20A",
    ampOutput: 20,
    // Renogy Voyager 20A New Edition (RCC20VOYP) datasheet: max PV input 55 V.
    pvMaxVoc: 25,
    width: 185,
    height: 105,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 25,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 20,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 20,
      },
    ],
    chargeAmpsTypical: 20,
    image: "mppt-renogy-voyager-20.webp",
  },
  {
    id: "mppt-renogy-commander-40",
    name: "Renogy Commander 40A",
    shortName: "Commander 40",
    subtitle: "MPPT solar controller 40A",
    category: "charger",
    compatibleVoltages: [12, 24],
    manufacturer: "Renogy",
    model: "Commander MPPT 40A",
    ampOutput: 40,
    pvMaxVoc: 150,
    width: 215,
    height: 125,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 50,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 50,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 40,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 40,
      },
    ],
    chargeAmpsTypical: 40,
  },
  {
    id: "mppt-renogy-commander-60",
    name: "Renogy Commander 60A",
    shortName: "Commander 60",
    subtitle: "MPPT solar controller 60A",
    category: "charger",
    compatibleVoltages: [12, 24, 36, 48],
    manufacturer: "Renogy",
    model: "Commander MPPT 60A",
    ampOutput: 60,
    pvMaxVoc: 150,
    width: 220,
    height: 130,
    terminals: [
      {
        id: "pv-pos",
        label: "PV +",
        polarity: "positive",
        direction: "input",
        side: "top",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "pv-neg",
        label: "PV −",
        polarity: "negative",
        direction: "input",
        side: "top",
        offset: 0.65,
        maxAmp: 60,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "bottom",
        offset: 0.35,
        maxAmp: 60,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "bottom",
        offset: 0.65,
        maxAmp: 60,
      },
    ],
    chargeAmpsTypical: 60,
  },
  {
    id: "shore-victron-multiplus-ii-48-5000",
    name: "Victron MultiPlus-II 48/5000",
    shortName: "MultiPlus-II 48/5000",
    subtitle: "Inverter/charger 48 V 5000 VA",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus-II 48/5000/70-50",
    ampOutput: 70,
    watts: 4000,
    ampDraw: 100,
    ampSurge: 200,
    compatibleVoltages: [48],
    width: 260,
    height: 160,
    terminals: [
      {
        id: "ac-in-l",
        label: "AC IN L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.18,
        maxAmp: 50,
      },
      {
        id: "ac-in-n",
        label: "AC IN N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.32,
        maxAmp: 50,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.6,
        maxAmp: 200,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.8,
        maxAmp: 200,
      },
      {
        id: "ac-out-l",
        label: "AC OUT L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "ac-out-n",
        label: "AC OUT N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 25,
      },
    ],
    image: "shore-victron-multiplus-ii-48-5000.webp",
  },
  {
    id: "shore-victron-quattro-48-5000",
    name: "Victron Quattro 48/5000",
    shortName: "Quattro 48/5000",
    subtitle: "Dual-input inverter/charger 48 V 5000 VA",
    category: "charger",
    manufacturer: "Victron",
    model: "Quattro 48/5000/70-100/100",
    ampOutput: 70,
    watts: 4000,
    ampDraw: 100,
    ampSurge: 200,
    compatibleVoltages: [48],
    width: 280,
    height: 170,
    terminals: [
      {
        id: "ac-in1-l",
        label: "AC IN1 L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.12,
        maxAmp: 100,
      },
      {
        id: "ac-in1-n",
        label: "AC IN1 N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.22,
        maxAmp: 100,
      },
      {
        id: "ac-in2-l",
        label: "AC IN2 L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.34,
        maxAmp: 100,
      },
      {
        id: "ac-in2-n",
        label: "AC IN2 N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.44,
        maxAmp: 100,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.65,
        maxAmp: 200,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.82,
        maxAmp: 200,
      },
      {
        id: "ac-out-l",
        label: "AC OUT L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "ac-out-n",
        label: "AC OUT N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 25,
      },
    ],
    image: "shore-victron-quattro-48-5000.webp",
  },
  {
    id: "shore-victron-multiplus-24-5000",
    name: "Victron MultiPlus 24/5000",
    shortName: "MultiPlus 24/5000",
    subtitle: "Inverter/charger 24V 5000VA",
    category: "charger",
    manufacturer: "Victron",
    model: "MultiPlus 24/5000/120-100",
    ampOutput: 120,
    watts: 4000,
    ampDraw: 200,
    ampSurge: 400,
    compatibleVoltages: [24],
    width: 250,
    height: 150,
    terminals: [
      {
        id: "ac-in-l",
        label: "AC IN L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.18,
        maxAmp: 50,
      },
      {
        id: "ac-in-n",
        label: "AC IN N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.32,
        maxAmp: 50,
      },
      {
        id: "dc-pos",
        label: "DC +",
        polarity: "positive",
        direction: "input",
        side: "left",
        offset: 0.6,
        maxAmp: 400,
      },
      {
        id: "dc-neg",
        label: "DC −",
        polarity: "negative",
        direction: "input",
        side: "left",
        offset: 0.8,
        maxAmp: 400,
      },
      {
        id: "ac-out-l",
        label: "AC OUT L",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 25,
      },
      {
        id: "ac-out-n",
        label: "AC OUT N",
        polarity: "neutral",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 25,
      },
    ],
  },
  {
    id: "shore-victron-bs-ip65-5",
    name: "Victron Blue Smart IP65 12/5",
    shortName: "BS IP65 5",
    subtitle: "Portable AC charger 5A",
    category: "charger",
    manufacturer: "Victron",
    model: "Blue Smart IP65 12/5",
    ampOutput: 5,
    chargeAmpsTypical: 5,
    width: 180,
    height: 95,
    terminals: [
      {
        id: "ac-l",
        label: "AC L",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.3,
        maxAmp: 3,
      },
      {
        id: "ac-n",
        label: "AC N",
        polarity: "neutral",
        direction: "input",
        side: "left",
        offset: 0.7,
        maxAmp: 3,
      },
      {
        id: "batt-pos",
        label: "BATT +",
        polarity: "positive",
        direction: "output",
        side: "right",
        offset: 0.35,
        maxAmp: 5,
      },
      {
        id: "batt-neg",
        label: "BATT −",
        polarity: "negative",
        direction: "output",
        side: "right",
        offset: 0.65,
        maxAmp: 5,
      },
    ],
  },
  // Manufacturer evidence and model boundaries: docs/research/component-manuals-common-50-2026-10-01.md.
  {
    "id": "dcdc-ctek-d250se",
    "name": "CTEK D250SE 20A",
    "shortName": "D250SE 20A",
    "subtitle": "DC-DC + solar charger",
    "category": "charger",
    "manufacturer": "CTEK",
    "model": "D250SE (1044 / 40-315)",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "ampDraw": 25,
    "ampOutput": 20,
    "pvMaxVoc": 23,
    "width": 210,
    "height": 120,
    "terminals": [
      {
        "id": "in-pos",
        "label": "ALT +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.3
      },
      {
        "id": "pv-pos",
        "label": "SOLAR +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "out-pos",
        "label": "SERVICE +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 20,
        "side": "right",
        "offset": 0.3
      },
      {
        "id": "gnd",
        "label": "EARTH −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 25,
        "side": "right",
        "offset": 0.7
      }
    ]
  },
  {
    "id": "dcdc-votronic-vcc-1212-30-ci",
    "name": "Votronic VCC 1212-30 CI",
    "shortName": "VCC 1212-30 CI",
    "subtitle": "DC-DC charger 30A",
    "category": "charger",
    "manufacturer": "Votronic",
    "model": "VCC 1212-30 CI (3360)",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "ampDraw": 39,
    "ampOutput": 30,
    "width": 210,
    "height": 115,
    "terminals": [
      {
        "id": "in-pos",
        "label": "START +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 39,
        "side": "left",
        "offset": 0.3
      },
      {
        "id": "in-neg",
        "label": "IN −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 39,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "out-pos",
        "label": "BORD +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.3
      },
      {
        "id": "out-neg",
        "label": "OUT −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 39,
        "side": "right",
        "offset": 0.7
      }
    ]
  },
  {
    "id": "shore-noco-genpro10x2",
    "name": "NOCO GENPRO10X2",
    "shortName": "GENPRO10X2",
    "subtitle": "Two-bank onboard charger",
    "category": "charger",
    "manufacturer": "NOCO",
    "model": "GENPRO10X2",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "ampOutput": 20,
    // NOCO GENPRO10X2: independent 10 A banks, 20 A aggregate. https://no.co/genpro10x2
    "outputCurrentLimitsA": { "bank1-pos": 10, "bank2-pos": 10 },
    "width": 215,
    "height": 150,
    "terminals": [
      {
        "id": "ac-in-l",
        "label": "AC L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 2.5,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "ac-in-n",
        "label": "AC N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 2.5,
        "side": "left",
        "offset": 0.75
      },
      {
        "id": "bank1-pos",
        "label": "BANK 1 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 10,
        "side": "right",
        "offset": 0.15
      },
      {
        "id": "bank1-neg",
        "label": "BANK 1 −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 10,
        "side": "right",
        "offset": 0.38
      },
      {
        "id": "bank2-pos",
        "label": "BANK 2 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 10,
        "side": "right",
        "offset": 0.62
      },
      {
        "id": "bank2-neg",
        "label": "BANK 2 −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 10,
        "side": "right",
        "offset": 0.85
      }
    ]
  },
  {
    "id": "shore-mastervolt-chargemaster-plus-12-50-3",
    "name": "Mastervolt ChargeMaster Plus 12/50-3",
    "shortName": "ChargeMaster Plus 50A",
    "subtitle": "Three-output shore charger",
    "category": "charger",
    "manufacturer": "Mastervolt",
    "model": "ChargeMaster Plus 12/50-3 (44310505)",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "ampOutput": 50,
    "width": 235,
    "height": 155,
    "terminals": [
      {
        "id": "ac-in-l",
        "label": "AC L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 7.1,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "ac-in-n",
        "label": "AC N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 7.1,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "ac-in-pe",
        "label": "AC PE",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 7.1,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "out1-pos",
        "label": "DC 1 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.15
      },
      {
        "id": "out2-pos",
        "label": "DC 2 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.38
      },
      {
        "id": "out3-pos",
        "label": "DC 3 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.62
      },
      {
        "id": "out-neg",
        "label": "COMMON −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.85
      }
    ]
  },
  {
    "id": "shore-xantrex-freedom-xc-pro-3000",
    "acVoltage": 120,
    "name": "Xantrex Freedom XC PRO 3000 120V",
    "shortName": "Freedom XC PRO 3000",
    "subtitle": "Inverter / charger 12V",
    "category": "charger",
    "manufacturer": "Xantrex",
    "model": "Freedom XC PRO 3000 (818-3010)",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "watts": 3000,
    "ampDraw": 304,
    "ampOutput": 150,
    "width": 240,
    "height": 180,
    "terminals": [
      {
        "id": "bat-pos",
        "label": "BATT +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 304,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "bat-neg",
        "label": "BATT −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 304,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "chassis",
        "label": "CHASSIS",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "ac-in-l",
        "label": "AC IN L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.1
      },
      {
        "id": "ac-in-n",
        "label": "AC IN N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.26
      },
      {
        "id": "ac-out-l",
        "label": "AC OUT L",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.48
      },
      {
        "id": "ac-out-n",
        "label": "AC OUT N",
        "polarity": "neutral",
        "direction": "output",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.64
      },
      {
        "id": "ac-pe",
        "label": "AC PE",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 50,
        "side": "right",
        "offset": 0.85
      }
    ],
    "simulateStates": {
      "default": "off",
      "options": [
        {
          "id": "inverting",
          "label": "Inverting",
          "role": "draw"
        },
        {
          "id": "charging",
          "label": "Charging",
          "role": "output"
        },
        {
          "id": "off",
          "label": "Off",
          "ampMultiplier": 0
        }
      ]
    }
  },
  {
    "id": "shore-schaudt-ebl119",
    "acVoltage": 230,
    "name": "Schaudt EBL 119 House Charger",
    "shortName": "EBL 119",
    "subtitle": "House charging scope 18A",
    "manufacturer": "Schaudt",
    "model": "EBL 119",
    "category": "charger",
    "voltage": 12,
    "compatibleVoltages": [
      12
    ],
    "ampOutput": 18,
    "width": 240,
    "height": 150,
    "terminals": [
      {
        "id": "ac-l",
        "label": "230V L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 1.9,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "ac-n",
        "label": "230V N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 1.9,
        "side": "left",
        "offset": 0.5
      },
      {
        "id": "ac-pe",
        "label": "PE",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 1.9,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "out-pos",
        "label": "House +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 18,
        "side": "right",
        "offset": 0.3
      },
      {
        "id": "out-neg",
        "label": "House −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 18,
        "side": "right",
        "offset": 0.7
      }
    ]
  },
  // Additional manufacturer-reviewed models; evidence: docs/research/component-manuals-additional-50-2026-10-01.md
  {
    "id": "dcdc-balmar-digital-duo-charge",
    "image": "dcdc-balmar-digital-duo-charge.webp",
    "name": "Balmar Digital Duo Charge DDC-12/24",
    "shortName": "DDC-12/24",
    "subtitle": "Regulated starter-battery maintenance charging up to 30 A from a charging house bank",
    "category": "charger",
    "manufacturer": "Balmar",
    "model": "DDC-12/24",
    "terminals": [
      {
        "id": "in-pos",
        "label": "HOUSE IN +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "out-pos",
        "label": "START OUT +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "gnd",
        "label": "CONTROL GND",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.75
      },
      {
        "id": "enable",
        "label": "ON/OFF",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.2
      },
      {
        "id": "temp",
        "label": "BATTERY TEMP",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.4
      },
      {
        "id": "solenoid",
        "label": "SOLENOID CONTROL",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.6
      },
      {
        "id": "mode",
        "label": "AUTO / MANUAL",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.8
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 30,
    "inputVoltage": 12,
    "outputVoltage": 12,
    "compatibleVoltages": [
      12,
      24
    ]
  },
  {
    "id": "pwm-morningstar-sunsaver-duo",
    "image": "pwm-morningstar-sunsaver-duo.webp",
    // SSD-25 manual PDF p. 13: every negative terminal shares a common return.
    internalDcConnections: [["pv-neg", "out-neg"], ["out-neg", "bank2-neg"]],
    "name": "Morningstar SunSaver Duo SSD-25",
    "shortName": "SSD-25",
    "subtitle": "12 V PWM solar controller with a shared 25 A budget for two lead-acid banks",
    "category": "charger",
    "manufacturer": "Morningstar",
    "model": "SSD-25",
    "terminals": [
      {
        "id": "pv-pos",
        "label": "PV +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "pv-neg",
        "label": "PV −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 25,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "out-pos",
        "label": "BANK 1 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 25,
        "side": "right",
        "offset": 0.1
      },
      {
        "id": "out-neg",
        "label": "BANK 1 −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 25,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "bank2-pos",
        "label": "BANK 2 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 25,
        "side": "right",
        "offset": 0.55
      },
      {
        "id": "bank2-neg",
        "label": "BANK 2 −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 25,
        "side": "right",
        "offset": 0.9500000000000001
      },
      {
        "id": "rts",
        "label": "REMOTE TEMP",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.3
      },
      {
        "id": "meter",
        "label": "REMOTE METER",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 25,
    "outputVoltage": 12,
    "pvMaxVoc": 30,
    "compatibleVoltages": [
      12
    ]
  },
  {
    "id": "mppt-genasun-gv10-lifepo4",
    "image": "mppt-genasun-gv10-lifepo4.webp",
    // GV-10 rev 4.1 PDF p. 5: battery negative is internally connected to PV negative.
    internalDcConnections: [["pv-neg", "out-neg"]],
    "name": "Genasun GV-10-Li-14.2V",
    "shortName": "GV-10-Li-14.2V",
    "subtitle": "140 W recommended PV / 10.5 A MPPT with fixed 14.2 V lithium charging",
    "category": "charger",
    "manufacturer": "Genasun",
    "model": "GV-10-Li-14.2V",
    "terminals": [
      {
        "id": "pv-pos",
        "label": "PV +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 10.5,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "pv-neg",
        "label": "PV −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 10.5,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "out-pos",
        "label": "BATTERY +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 10.5,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "out-neg",
        "label": "BATTERY −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 10.5,
        "side": "right",
        "offset": 0.65
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 10.5,
    "outputVoltage": 12.8,
    "pvMaxVoc": 34,
    "compatibleVoltages": [
      12
    ]
  },
  {
    "id": "dcdc-votronic-standby-pro",
    "image": "dcdc-votronic-standby-pro.webp",
    "name": "Votronic StandBy-Charger Pro 3063",
    "shortName": "StandBy-Charger Pro 3063",
    "subtitle": "Adjustable 12 V to 12 V maintenance charger, up to 8 A output and 12 A input",
    "category": "charger",
    "manufacturer": "Votronic",
    "model": "StandBy-Charger Pro 3063",
    "terminals": [
      {
        "id": "in-pos",
        "label": "IN +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 12,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "in-neg",
        "label": "IN −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 12,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "out-pos",
        "label": "OUT +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 8,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "out-neg",
        "label": "OUT −",
        "polarity": "negative",
        "direction": "output",
        "maxAmp": 8,
        "side": "right",
        "offset": 0.65
      },
      {
        "id": "en",
        "label": "EN",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.3
      },
      {
        "id": "dis",
        "label": "DIS / D+",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 8,
    "maxInputA": 12,
    "inputVoltage": 12,
    "outputVoltage": 12,
    "compatibleVoltages": [
      12
    ]
  },
  {
    "id": "charger-votronic-vbcs-30-20-250-ci",
    "image": "charger-votronic-vbcs-30-20-250-ci.webp",
    "name": "Votronic VBCS 30/20/250 Triple CI 3242",
    "shortName": "VBCS 30/20/250 Triple CI (3242)",
    "subtitle": "Combined 30 A alternator booster, 20 A mains charger and 250 W solar controller",
    "category": "charger",
    "manufacturer": "Votronic",
    "model": "VBCS 30/20/250 Triple CI (3242)",
    "terminals": [
      {
        "id": "start-pos",
        "label": "START II +",
        "polarity": "positive",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.15
      },
      {
        "id": "out-pos",
        "label": "BORD I +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 30,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "out-neg",
        "label": "COMMON −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.6
      },
      {
        "id": "pv-pos",
        "label": "SOLAR +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 15,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "ac-in-l",
        "label": "AC L",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "ac-in-n",
        "label": "AC N",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.8
      },
      {
        "id": "ac-in-pe",
        "label": "AC PE",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.95
      },
      {
        "id": "d-plus",
        "label": "D+",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.1
      },
      {
        "id": "temp",
        "label": "T / T",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.25
      },
      {
        "id": "sense",
        "label": "S+ / S−",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.4
      },
      {
        "id": "bms",
        "label": "SW / BMS",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.55
      },
      {
        "id": "ci",
        "label": "CI BUS",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      },
      {
        "id": "aes",
        "label": "AES",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 1,
        "side": "bottom",
        "offset": 0.85
      }
    ],
    "width": 180,
    "height": 130,
    "maxInputA": 42,
    "inputVoltage": 12,
    "outputVoltage": 12,
    "pvMaxVoc": 36,
    "compatibleVoltages": [
      12
    ],
    "simulateStates": {
      "default": "off",
      "options": [
        {
          "id": "booster",
          "label": "Alternator booster (up to 30 A); output unpredicted",
          "intraLinks": []
        },
        {
          "id": "mains",
          "label": "Mains (20 A shared); output unpredicted",
          "intraLinks": []
        },
        {
          "id": "solar",
          "label": "Solar (up to 18 A shared); output unpredicted",
          "intraLinks": []
        },
        {
          "id": "off",
          "label": "Off",
          "ampMultiplier": 0,
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "pwm-schaudt-lr1218",
    "image": "pwm-schaudt-lr1218.webp",
    "name": "Schaudt LR 1218",
    "shortName": "LR 1218",
    "subtitle": "12 V PWM solar regulator for lead-acid / gel banks of at least 55 Ah",
    "category": "charger",
    "manufacturer": "Schaudt",
    "model": "LR 1218",
    "terminals": [
      {
        "id": "pv-pos",
        "label": "SOLAR +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 18,
        "side": "left",
        "offset": 0.25
      },
      {
        "id": "pv-neg",
        "label": "SOLAR −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 18,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "out-pos",
        "label": "HOUSE +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 18,
        "side": "right",
        "offset": 0.2
      },
      {
        "id": "out-neg",
        "label": "BATTERY COMMON −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.5
      },
      {
        "id": "starter-pos",
        "label": "STARTER +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 18,
        "side": "right",
        "offset": 0.8
      },
      {
        "id": "display",
        "label": "OPTIONAL DISPLAY",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.5
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 18,
    "outputVoltage": 12,
    "pvMaxVoc": 25,
    "compatibleVoltages": [
      12
    ]
  },
  {
    "id": "charger-sterling-ab1280",
    "image": "charger-sterling-ab1280.webp",
    "name": "Sterling Pro Alt C AB1280",
    "shortName": "AB1280",
    "subtitle": "12 V alternator-to-battery charger for an alternator rated up to 80 A",
    "category": "charger",
    "manufacturer": "Sterling",
    "model": "AB1280",
    "terminals": [
      {
        "id": "alt-pos",
        "label": "ALTERNATOR IN +",
        "polarity": "positive",
        "direction": "input",
        "maxAmp": 80,
        "side": "left",
        "offset": 0.2
      },
      {
        "id": "out-pos",
        "label": "DOMESTIC +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.25
      },
      {
        "id": "starter-pos",
        "label": "STARTER +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 0,
        "side": "right",
        "offset": 0.7
      },
      {
        "id": "gnd",
        "label": "COMMON −",
        "polarity": "negative",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.7
      },
      {
        "id": "ign",
        "label": "IGNITION",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.1
      },
      {
        "id": "sense",
        "label": "DOMESTIC SENSE",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.3
      },
      {
        "id": "batt-temp",
        "label": "BATTERY TEMP",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.5
      },
      {
        "id": "alt-temp",
        "label": "ALTERNATOR TEMP",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.7
      },
      {
        "id": "remote",
        "label": "REMOTE",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.9
      }
    ],
    "width": 180,
    "height": 130,
    "inputVoltage": 12,
    "outputVoltage": 12,
    "compatibleVoltages": [
      12
    ],
    "simulateStates": {
      "default": "off",
      "options": [
        {
          "id": "on",
          "label": "Alternator charging; output unpredicted",
          "intraLinks": []
        },
        {
          "id": "off",
          "label": "Off",
          "ampMultiplier": 0,
          "intraLinks": []
        }
      ]
    }
  },
  {
    "id": "shore-promariner-pronautic-1220p",
    "image": "shore-promariner-pronautic-1220p.webp",
    "name": "ProMariner ProNautic 1220P",
    "shortName": "ProNautic 1220P",
    "subtitle": "12 V shore charger with three positive bank outputs sharing 20 A total and one common charge profile",
    "category": "charger",
    "manufacturer": "ProMariner",
    "model": "ProNautic 1220P",
    "terminals": [
      {
        "id": "ac-in-l",
        "label": "AC LINE",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.15
      },
      {
        "id": "ac-in-n",
        "label": "AC NEUTRAL",
        "polarity": "neutral",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.4
      },
      {
        "id": "ac-in-pe",
        "label": "AC EARTH",
        "polarity": "ground",
        "direction": "input",
        "maxAmp": 0,
        "side": "left",
        "offset": 0.65
      },
      {
        "id": "chassis",
        "label": "DC CHASSIS EARTH",
        "polarity": "ground",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.2
      },
      {
        "id": "out-pos",
        "label": "BANK 1 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 20,
        "side": "right",
        "offset": 0.15
      },
      {
        "id": "bank2-pos",
        "label": "BANK 2 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 20,
        "side": "right",
        "offset": 0.35
      },
      {
        "id": "bank3-pos",
        "label": "BANK 3 +",
        "polarity": "positive",
        "direction": "output",
        "maxAmp": 20,
        "side": "right",
        "offset": 0.55
      },
      {
        "id": "out-neg",
        "label": "DC COMMON −",
        "polarity": "negative",
        "direction": "bidi",
        "maxAmp": 20,
        "side": "right",
        "offset": 0.8
      },
      {
        "id": "temp",
        "label": "BATTERY TEMP",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.55
      },
      {
        "id": "remote",
        "label": "REMOTE",
        "polarity": "signal",
        "direction": "bidi",
        "maxAmp": 0,
        "side": "bottom",
        "offset": 0.85
      }
    ],
    "width": 180,
    "height": 130,
    "ampOutput": 20,
    "outputVoltage": 12,
    "compatibleVoltages": [
      12
    ]
  },
];
